import React, { useState } from 'react'
import { Grid, Typography, Box, useTheme, CircularProgress, Button } from '@material-ui/core'
import { motion, AnimatePresence } from 'framer-motion'
import { CheckCircle as SelectedIcon, Science as ConfigIcon } from '@material-ui/icons'
import { useCarrierConfigs } from '../../../../api/carrierAnalysis/list'
import {
  ClinicalAnalysisContainer,
  ClinicalAnalysisCard,
  ClinicalAnalysisIcon,
  ClinicalAnalysisTitle,
  ClinicalAnalysisDescription,
  ClinicalAnalysisRequirement,
  ClinicalModeContainer,
  pageVariants,
  clinicalCardVariants,
} from '../styles/StyledComponents'
import { alpha } from '@material-ui/core/styles'

const CarrierConfigSelect = ({ selectedConfigId, onConfigSelect }) => {
  const theme = useTheme()
  const {
    query: { data: configs, isLoading, error },
  } = useCarrierConfigs()

  const [expandedIds, setExpandedIds] = useState(new Set())

  const handleConfigSelect = (config) => {
    onConfigSelect(config)
  }

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  if (isLoading) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            gap: 3,
          }}
        >
          <CircularProgress size={48} sx={{ color: theme.palette.primary.main }} />
          <Typography variant="h6" sx={{ color: theme.palette.text.secondary }}>
            Loading carrier screening configurations...
          </Typography>
        </Box>
      </motion.div>
    )
  }

  if (error) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            gap: 2,
          }}
        >
          <Typography variant="h6" sx={{ color: theme.palette.error.main }}>
            Error loading configurations
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            Please try again later or contact support if the problem persists.
          </Typography>
        </Box>
      </motion.div>
    )
  }

  const configList = configs?.results || []

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography
          variant="h5"
          sx={{
            color: theme.palette.primary.main,
            fontWeight: 700,
            mb: 1.5,
            fontSize: '1.4rem',
            letterSpacing: '0.5px',
          }}
        >
          Select Carrier Screening Configuration
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: '1rem',
            maxWidth: '700px',
            mx: 'auto',
            lineHeight: 1.6,
          }}
        >
          Choose from our curated carrier screening panels designed for comprehensive genetic analysis.
        </Typography>
      </Box>

      <ClinicalAnalysisContainer>
        <ClinicalModeContainer>
          <AnimatePresence mode="wait">
            {configList.map((config, index) => (
              <motion.div
                key={config.id}
                custom={index}
                variants={clinicalCardVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                whileHover="hover"
                whileTap="tap"
                style={{ flex: 1 }}
              >
                <ClinicalAnalysisCard
                  selected={selectedConfigId === config.id}
                  onClick={() => handleConfigSelect(config)}
                  sx={{
                    position: 'relative',
                    minHeight: '240px',
                    padding: theme.spacing(2.5),
                  }}
                >
                  {selectedConfigId === config.id && (
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{
                        type: 'spring',
                        stiffness: 600,
                        damping: 25,
                        mass: 0.4,
                      }}
                      style={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        zIndex: 2,
                      }}
                    >
                      <SelectedIcon
                        sx={{
                          color: theme.palette.primary.main,
                          fontSize: '1.5rem',
                          filter: `drop-shadow(0 2px 4px ${alpha(theme.palette.primary.main, 0.25)})`,
                        }}
                      />
                    </motion.div>
                  )}

                  <ClinicalAnalysisIcon
                    selected={selectedConfigId === config.id}
                    sx={{
                      width: 36,
                      height: 36,
                      marginBottom: theme.spacing(1.5),
                    }}
                  >
                    <ConfigIcon sx={{ fontSize: '1.3rem' }} />
                  </ClinicalAnalysisIcon>

                  <ClinicalAnalysisTitle
                    selected={selectedConfigId === config.id}
                    sx={{
                      fontSize: '1rem',
                      marginBottom: theme.spacing(1),
                      lineHeight: 1.2,
                    }}
                  >
                    {config.panel_title}
                  </ClinicalAnalysisTitle>

                  <Box sx={{ width: '100%' }}>
                    <ClinicalAnalysisDescription
                      sx={{
                        fontSize: '1rem',
                        lineHeight: 1.65,
                        marginBottom: theme.spacing(1),
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: expandedIds.has(config.id) ? 'unset' : 4,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'normal',
                      }}
                    >
                      {config.description}
                    </ClinicalAnalysisDescription>
                    {config.description?.length > 240 && (
                      <Button
                        size="small"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleExpanded(config.id)
                        }}
                        aria-expanded={expandedIds.has(config.id)}
                        aria-label={expandedIds.has(config.id) ? 'Show less description' : 'Show more description'}
                        sx={{ textTransform: 'none' }}
                      >
                        {expandedIds.has(config.id) ? 'Show less' : 'Show more'}
                      </Button>
                    )}
                  </Box>

                  <Box sx={{ mt: 'auto', textAlign: 'center' }}>
                    <ClinicalAnalysisRequirement selected={selectedConfigId === config.id} sx={{ fontSize: '0.75rem' }}>
                      {config.samples_required} Sample{config.samples_required !== 1 ? 's' : ''} Required
                    </ClinicalAnalysisRequirement>
                  </Box>

                  <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center' }}>
                    <Button
                      variant={selectedConfigId === config.id ? 'contained' : 'outlined'}
                      color="primary"
                      size="small"
                      aria-pressed={selectedConfigId === config.id}
                      aria-label={`Select ${config.panel_title}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleConfigSelect(config)
                      }}
                    >
                      {selectedConfigId === config.id ? 'Selected' : 'Select'}
                    </Button>
                  </Box>
                </ClinicalAnalysisCard>
              </motion.div>
            ))}
          </AnimatePresence>
        </ClinicalModeContainer>
      </ClinicalAnalysisContainer>

      {configList.length === 0 && !isLoading && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            color: theme.palette.text.secondary,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            No carrier screening configurations available
          </Typography>
          <Typography variant="body2">Please contact your administrator to set up carrier screening panels.</Typography>
        </Box>
      )}
    </motion.div>
  )
}

export default CarrierConfigSelect
